import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { BadgeRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { memo, useMemo, useState } from "react";

export const CustomerTagsSelector = memo(function CustomerTagsSelector({
  selectedTags,
  onTagsSelected,
}: {
  selectedTags: string[];
  onTagsSelected: (tags: string[]) => void;
}) {
  const [searchString, setSearchString] = useState("");
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const tagsLoad = useLoad(
    async (signal) => {
      try {
        const response = await merchantRpcClient.getCustomerTags(null, {
          signal,
        });

        if (!response.tags) {
          return [];
        }

        return response.tags;
      } catch (error) {
        console.error("Error searching customer tags:", error);
        return [];
      }
    },
    [merchantRpcClient],
  );

  const tagOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    if (!tagsLoad.value) {
      return [];
    }

    // Load all tags from backend then do search filtering on frontend
    const loadedTags = tagsLoad.value.filter((tag) =>
      tag.toLowerCase().includes(searchString.toLowerCase()),
    );

    const allTags = new Set([...loadedTags, ...selectedTags]);

    return Array.from(allTags).map((tag) => ({
      id: tag,
      value: tag,
      type: "badge",
      badge: { text: tag, color: "gray" },
    }));
  }, [tagsLoad.value, selectedTags, searchString]);

  const selectedTagOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return selectedTags.map((tag) => ({
      id: tag,
      value: tag,
      type: "badge",
      badge: { text: tag, color: "gray" },
    }));
  }, [selectedTags]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      onTagsSelected(items.map((item) => item.value));
    },
  );

  return (
    <Flex w="full">
      <RedoTagsDropdownInput<string>
        description={
          selectedTags.length > 0
            ? `${selectedTags.length} tag${selectedTags.length !== 1 ? "s" : ""} selected`
            : undefined
        }
        loading={tagsLoad.pending}
        options={tagOptions}
        placeholder="Enter or select tags"
        searchPlaceholder="Search for customer tags..."
        searchString={searchString}
        selectedOptions={selectedTagOptions}
        setSearchString={setSearchString}
        setSelectedOptions={handleSelectionChange}
        size="xs"
      />
    </Flex>
  );
});
